import { diffInMinutes } from '@zurich/common/forms';

export interface CompressImageBlobProps {
  file: File;
  maxFileAge: number;
  maxWidth?: number;
  mimeType?: 'image/jpeg';
  quality?: number;
}

/**
 * `compressImageBlob` is used to compress uploaded
 * images within the browser using an HTML canvas.
 *
 * Uses toBlob() for conversion, @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
 *
 * @param file file to compress
 * @param compressionQuality number between 0 and 1 which defines compressed image quality
 * @param maxFileAge the allowed age of a file in minutes
 * @param maxWidth the maximum width of the image
 * @param mimeType target image format as MIME type
 * @param quality number between 0 and 1.0
 * @param
 * @returns compressed image data encoded in base64
 */
export function compressImageBlob({
  file,
  maxFileAge,
  maxWidth = 2000,
  mimeType = 'image/jpeg',
  quality = 0.6,
}: CompressImageBlobProps): Promise<string> {
  const reader = new FileReader();

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;

  return new Promise<string>(
    (resolve, reject) =>
      (img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(browserNotSupported);
          return;
        }

        const needsScaling = img.naturalWidth > maxWidth;
        const scaleFactor = needsScaling ? maxWidth / img.naturalWidth : 1.0;

        if (needsScaling) {
          console.debug(
            'Uploaded image exceeds maximum width. Applying scale factor',
            { width: canvas.width, height: canvas.height, scaleFactor }
          );
        }

        const canvasWidth = scaleFactor * img.naturalWidth;
        const canvasHeight = scaleFactor * img.naturalHeight;

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

        ctx.canvas.toBlob(
          (blob) => {
            const lastModified = file.lastModified;
            const isPhotoOlderThanThreeMinutes =
              diffInMinutes(new Date(), lastModified) > maxFileAge;

            if (isPhotoOlderThanThreeMinutes) {
              reject(fileTooOld);
            }

            if (blob) {
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                  const base64Data = reader.result;
                  resolve(base64Data);
                } else {
                  reject(base64Error);
                }
              };
              reader.onerror = () => {
                reject(base64Error);
              };
            } else {
              reject(base64Error);
            }
          },
          mimeType,
          quality
        );
      })
  );
}

export const fileSizeError = 'fileTooLarge';
export const base64Error = 'noBase64';
export const fileTooOld = 'fileTooOld';
export const browserNotSupported = 'browserNotSupported';
